import { clients } from '@/lib/https';
import {
  readWorkflowResponse,
  WorkflowRefusedError,
  type WorkflowResponse,
} from '@/lib/workflow-webhook';

/**
 * The HRM API: one hand-written Blocks Function per module (blocks/functions/hrm-<module>), one route per
 * action. Every write the app can't make directly (the data rules let only `super_admin`
 * write these schemas) goes through one: it checks the caller's permission, ownership, department and
 * state, saves with the service credential and answers the response envelope (`WorkflowResponse`).
 * Background work (the attendance scheduler and import, notifications and mail) stays in workflows.
 *
 * Each function's URL is in `VITE_HRM_<MODULE>_FUNCTION_URL` (`blocks/functions/deploy.mjs --write-env`);
 * the route is the action's name in kebab case (`TOIL_REQUEST` -> `<url>/toil-request`).
 */
const FUNCTION_URLS = {
  attendance: {
    url: import.meta.env.VITE_HRM_ATTENDANCE_FUNCTION_URL,
    envName: 'VITE_HRM_ATTENDANCE_FUNCTION_URL',
  },
  leave: {
    url: import.meta.env.VITE_HRM_LEAVE_FUNCTION_URL,
    envName: 'VITE_HRM_LEAVE_FUNCTION_URL',
  },
  lunch: {
    url: import.meta.env.VITE_HRM_LUNCH_FUNCTION_URL,
    envName: 'VITE_HRM_LUNCH_FUNCTION_URL',
  },
  overtime: {
    url: import.meta.env.VITE_HRM_OVERTIME_FUNCTION_URL,
    envName: 'VITE_HRM_OVERTIME_FUNCTION_URL',
  },
  serviceDesk: {
    url: import.meta.env.VITE_HRM_SERVICE_DESK_FUNCTION_URL,
    envName: 'VITE_HRM_SERVICE_DESK_FUNCTION_URL',
  },
  toil: { url: import.meta.env.VITE_HRM_TOIL_FUNCTION_URL, envName: 'VITE_HRM_TOIL_FUNCTION_URL' },
} as const;

type FunctionModule = keyof typeof FUNCTION_URLS;

/** Every action and the function that answers it. */
const ROUTES = {
  ATTENDANCE_CLOCK_IN: 'attendance',
  ATTENDANCE_CLOCK_OUT: 'attendance',
  ATTENDANCE_BREAK_START: 'attendance',
  ATTENDANCE_BREAK_END: 'attendance',
  ATTENDANCE_CORRECTION_REQUEST: 'attendance',
  ATTENDANCE_CORRECTION_APPROVE: 'attendance',
  ATTENDANCE_CORRECTION_REJECT: 'attendance',
  ATTENDANCE_FINE_WAIVE: 'attendance',
  LEAVE_REQUEST_SUBMIT: 'leave',
  LEAVE_REQUEST_WITHDRAW: 'leave',
  LEAVE_REQUEST_CANCEL: 'leave',
  LEAVE_REQUEST_REVIEW: 'leave',
  LEAVE_REQUEST_ARCHIVE: 'leave',
  SICK_LEAVE_REQUEST: 'leave',
  LUNCH_CREATE: 'lunch',
  LUNCH_CANCEL: 'lunch',
  OVERTIME_PAYOFF: 'overtime',
  NOC_APPLICATION_SUBMIT: 'serviceDesk',
  NOC_APPLICATION_WITHDRAW: 'serviceDesk',
  NOC_APPLICATION_REVIEW: 'serviceDesk',
  NOC_APPLICATION_GENERATE: 'serviceDesk',
  NOC_APPLICATION_DELETE: 'serviceDesk',
  BYOD_APPLICATION_SUBMIT: 'serviceDesk',
  BYOD_APPLICATION_WITHDRAW: 'serviceDesk',
  BYOD_APPLICATION_REVIEW: 'serviceDesk',
  BUSINESS_CARD_APPLICATION_SUBMIT: 'serviceDesk',
  BUSINESS_CARD_APPLICATION_WITHDRAW: 'serviceDesk',
  BUSINESS_CARD_APPLICATION_STATUS: 'serviceDesk',
  ID_CARD_APPLICATION_SUBMIT: 'serviceDesk',
  ID_CARD_APPLICATION_WITHDRAW: 'serviceDesk',
  ID_CARD_APPLICATION_STATUS: 'serviceDesk',
  METLIFE_APPLICATION_SUBMIT: 'serviceDesk',
  METLIFE_APPLICATION_WITHDRAW: 'serviceDesk',
  METLIFE_APPLICATION_STATUS: 'serviceDesk',
  METLIFE_APPLICATION_CANCEL: 'serviceDesk',
  TOIL_REQUEST: 'toil',
  TOIL_WITHDRAW: 'toil',
  TOIL_REVIEW: 'toil',
  TOIL_ARCHIVE: 'toil',
  TOIL_DELETE: 'toil',
} as const satisfies Record<string, FunctionModule>;

export type HrmApiAction = keyof typeof ROUTES;

/** The URL of `action`: its function's URL plus the route. */
export const hrmApiUrl = (action: HrmApiAction): string => {
  const { url, envName } = FUNCTION_URLS[ROUTES[action]];
  if (!url) throw new Error(`${envName} is not configured`);
  return `${url.replace(/\/$/, '')}/${action.toLowerCase().replace(/_/g, '-')}`;
};

/**
 * Calls an HRM API action. `clients` adds the caller's bearer token and x-blocks-key (and sends the session
 * cookie), which the function's Token trigger authorizes. Answers the response envelope as sent.
 */
export const postHrmApi = async (
  action: HrmApiAction,
  body: Record<string, unknown> = {}
): Promise<unknown> => clients.post(hrmApiUrl(action), JSON.stringify(body));

/**
 * Calls an action whose refusal should fail the mutation: a refusal throws `WorkflowRefusedError` (its
 * translation key), an answer without an envelope throws `failureCode`; on success the envelope is returned.
 */
export const runHrmApi = async <TData = unknown>(
  action: HrmApiAction,
  body: Record<string, unknown>,
  failureCode: string
): Promise<WorkflowResponse<TData & { MessageKey?: string }>> => {
  const result = readWorkflowResponse<TData & { MessageKey?: string }>(
    await postHrmApi(action, body),
    failureCode
  );
  if (!result.IsSuccessful)
    throw new WorkflowRefusedError(result.Data?.MessageKey || result.Message);
  return result;
};
